import { CalendarClock, Database, FolderOpen, Pencil, Plus, Server as ServerIcon, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { flatPrimary, innerSurface, toneText, toneWash } from '@/components/surface/tones';
import { IconTile, SectionHeader, StatusPill } from '@/components/surface/ui';
import { cn } from '@/lib/utils';
import { ActionMenu } from '@/modules/backups/ActionMenu';
import { SETUP_CARD_GRID } from '@/modules/backups/setup-layout';
import type { BackupSchedule, BackupSource } from '@/types/api';

/**
 * What is backed up: each source, the server it is on, and what it holds.
 *
 * Drawn as one half of a block, with where the copies go ({@see DestinationsSection}) as the other:
 * the two answer one question between them, and the page places the block.
 *
 * ## A source is where the data is, not how it got there
 *
 * A directory, a database, or both, on one server — whatever put them there. Each card says those
 * three things and whether anything protects it automatically, because a source with no schedule is
 * one somebody meant to protect and has not.
 *
 * ## A server that has gone is said on the card
 *
 * A source whose server is no longer on the account keeps its history, but nothing can be backed up
 * or restored until it points somewhere real. That is the card's one amber state, with the fix in it.
 *
 * Removing a source forgets it and its schedule; the backups already taken and the data on the server
 * stay, and the menu says both before anyone clicks.
 */
export function SourcesSection({
  sources,
  schedules,
  permitsChanges,
  removing,
  onAdd,
  onEdit,
  onRemove,
  className,
}: {
  sources: BackupSource[];
  schedules: BackupSchedule[];
  permitsChanges: boolean;
  removing: boolean;
  onAdd: () => void;
  onEdit: (source: BackupSource) => void;
  onRemove: (id: string) => void;
  className?: string | undefined;
}) {
  return (
    <div id="sources" className={cn('min-w-0 scroll-mt-24', className)}>
      <SectionHeader
        icon={FolderOpen}
        iconTone="primary"
        title="What is backed up"
        description="Directories and databases on your servers."
        actions={
          sources.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={onAdd} disabled={!permitsChanges} className="text-muted-foreground hover:text-foreground">
              <Plus />
              Add source
            </Button>
          ) : undefined
        }
      />

      {sources.length === 0 ? (
        <div className="px-5 pb-5">
          <p className="text-[13px] text-muted-foreground">
            Nothing to back up yet. Add a source: a directory such as /var/www/example, a MariaDB, MySQL or PostgreSQL
            database, or both, on one of your servers.
          </p>
          <Button size="sm" onClick={onAdd} disabled={!permitsChanges} className={cn('mt-3', flatPrimary)}>
            <Plus />
            Add source
          </Button>
        </div>
      ) : (
        <ul className={SETUP_CARD_GRID}>
          {sources.map((source) => (
            <SourceCard
              key={source.id}
              source={source}
              schedule={schedules.find((schedule) => schedule.source_id === source.id)}
              permitsChanges={permitsChanges}
              removing={removing}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function SourceCard({
  source,
  schedule,
  permitsChanges,
  removing,
  onEdit,
  onRemove,
}: {
  source: BackupSource;
  schedule: BackupSchedule | undefined;
  permitsChanges: boolean;
  removing: boolean;
  onEdit: (source: BackupSource) => void;
  onRemove: (id: string) => void;
}) {
  const serverGone = source.server_name === null;
  const state = serverGone
    ? ({ tone: 'warning', label: 'Server removed' } as const)
    : schedule === undefined
      ? ({ tone: 'muted', label: 'No schedule' } as const)
      : schedule.is_stale
        ? ({ tone: 'danger', label: 'Not running' } as const)
        : schedule.enabled
          ? ({ tone: 'success', label: 'Scheduled' } as const)
          : ({ tone: 'muted', label: 'Paused' } as const);

  return (
    <li className={cn(innerSurface, 'flex flex-col')}>
      <div className="flex items-start gap-3 p-3.5 pb-3">
        <IconTile icon={source.has_directory ? FolderOpen : Database} tone="primary" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{source.name}</p>
          <p className="flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground">
            <ServerIcon className="size-3 shrink-0" aria-hidden />
            <span className="truncate">{source.server_name ?? 'A server no longer on your account'}</span>
          </p>
        </div>
        <ActionMenu
          label={`More actions for ${source.name}`}
          items={[
            {
              label: 'Edit source',
              icon: Pencil,
              disabled: !permitsChanges,
              onSelect: () => onEdit(source),
            },
            {
              label: 'Remove source',
              icon: Trash2,
              tone: 'danger',
              separated: true,
              hint: 'Its schedule stops. Backups already taken and the data on the server are kept.',
              disabled: removing,
              onSelect: () => onRemove(source.id),
            },
          ]}
        />
      </div>

      {serverGone && (
        <p className={cn('mx-3.5 mb-1 rounded-md px-3 py-2 text-xs text-foreground/85', toneWash.warning)}>
          Nothing can be backed up or restored until this points at a server on your account. Edit the source to choose
          one, or remove it.
        </p>
      )}

      <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-3.5 pb-3.5 pt-2 text-xs">
        <Detail label="Directory" title={source.directory}>
          {source.has_directory ? (
            <span className="font-mono text-[11px]">{source.directory}</span>
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </Detail>
        <Detail label="Database" title={source.has_database ? `${source.database_engine_label ?? ''} · ${source.database_name}` : undefined}>
          {source.has_database ? (
            <span>
              <span className="font-mono text-[11px]">{source.database_name}</span>
              <span className="text-muted-foreground"> · {source.database_engine_label}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </Detail>
        <Detail label="Schedule" title={schedule !== undefined ? `${schedule.frequency_label} to ${schedule.destination_name}` : undefined}>
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <StatusPill tone={state.tone} label={state.label} />
            {schedule !== undefined && !serverGone && (
              <span className={cn('truncate', schedule.is_stale ? toneText.danger : 'text-muted-foreground')}>
                <CalendarClock className="mr-1 inline size-3 align-[-2px]" aria-hidden />
                {schedule.frequency_label.toLowerCase()}
              </span>
            )}
          </span>
        </Detail>
      </dl>
    </li>
  );
}

function Detail({ label, title, children }: { label: string; title?: string | undefined; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-foreground/90" title={title}>
        {children}
      </dd>
    </>
  );
}
